import {
  HHButton,
  HHIconButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { BillingType, ClientType, PartnershipType } from '../../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../../utils/bills-utils';
import {
  BillingTypeFilter,
  ClientFilter,
  CSVInputFilter,
  DateRangeFilter,
  InsurerFilter,
  PartnershipFilter,
  SourceFilter,
  SubmissionStatusFilter,
} from '../../../form';
import { AutocompleteSelectOption } from '../../../grid-components/grid-types';
import {
  InsurersType,
  SubmissionManagementGridFilterModelType,
} from '../../bill-grid-components/bill-management-types';
import { BillSource } from '../../bill-types';
import { extractIds, formatDateForQuery, parseIds } from '../../utils';
import { defaultSubmissionManagementGridFilterModel } from '../submission-level/submission-management-grid';
import { SubmissionAdvancedFiltersValidationSchema } from './validation-schema';

export interface SubmissionAdvancedFiltersValues {
  submissionIds: number[];
  transactionIds: string[];
  netsuiteIds: string[];
  submissionStatusTypes: AutocompleteSelectOption[];
  submissionDateRange: (Date | null)[];
  creditMemoIds: string[];
  billIds: number[];
  clients: ClientType[];
  insurers: InsurersType[];
  partnerships: PartnershipType[];
  billingTypes: AutocompleteSelectOption[];
  source: string | null;
  sourceIds: number[];
  sourceDateRange: (Date | null)[];
  userIds: number[];
}

export interface SubmissionAdvancedFiltersProps {
  setFilterParams: (params: SubmissionManagementGridFilterModelType) => void;
  filterParams: SubmissionManagementGridFilterModelType;
  clients: ClientType[];
  insurers: InsurersType[];
  partnerships: PartnershipType[];
  billingTypes: BillingType[];
  hasError: boolean;
  filterToggle: () => void;
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
}

export const SubmissionAdvancedFilters = ({
  setFilterParams,
  filterParams,
  clients,
  insurers,
  partnerships,
  billingTypes,
  hasError,
  setPaginationModel,
  filterToggle,
}: SubmissionAdvancedFiltersProps): JSX.Element => {
  const {
    submissionIds,
    transactionIds,
    netsuiteIds,
    creditMemoIds,
    billIds,
    clientIds,
    insurerIds,
    source,
    sourceIds,
    sourceStartDate,
    sourceEndDate,
    submissionStatuses,
    submissionStartDate,
    submissionEndDate,
    userIds,
    partnershipIds,
    billingTypes: filteredBillingTypes,
  } = filterParams;

  const defaultValues: SubmissionAdvancedFiltersValues = {
    submissionIds: submissionIds ?? [],
    billIds: billIds ?? [],
    userIds: userIds ?? [],
    transactionIds: transactionIds ?? [],
    netsuiteIds: netsuiteIds ?? [],
    creditMemoIds: creditMemoIds ?? [],
    submissionStatusTypes:
      submissionStatuses?.map(status => ({
        value: status,
        label: status,
      })) ?? [],
    submissionDateRange: [
      dayjs(submissionStartDate).isValid()
        ? dayjs(submissionStartDate).toDate()
        : null,
      dayjs(submissionEndDate).isValid()
        ? dayjs(submissionEndDate).toDate()
        : null,
    ],
    source: source ?? BillSource.ALL,
    sourceIds: sourceIds ?? [],
    sourceDateRange: [
      dayjs(sourceStartDate).isValid() ? dayjs(sourceStartDate).toDate() : null,
      dayjs(sourceEndDate).isValid() ? dayjs(sourceEndDate).toDate() : null,
    ],
    clients: clients.filter(client => clientIds.includes(client.id)),
    insurers: insurers.filter(insurer => insurerIds.includes(insurer.id)),
    partnerships: partnerships.filter(partnership =>
      partnershipIds.includes(partnership.id),
    ),
    billingTypes: billingTypes
      .filter(billingType => filteredBillingTypes.includes(billingType.value))
      .map(billingType => ({
        value: billingType.value,
        label: capitalizeFirstLetterAndSpaceSnakeString(billingType.value),
      })),
  };

  const parseFormValues = (data: SubmissionAdvancedFiltersValues): void => {
    const {
      submissionIds,
      transactionIds,
      netsuiteIds,
      submissionStatusTypes,
      submissionDateRange,
      creditMemoIds,
      billIds,
      clients,
      insurers,
      partnerships,
      billingTypes,
      source,
      sourceIds,
      sourceDateRange,
      userIds,
    } = data;

    const formattedData = {
      submissionIds: parseIds(submissionIds),
      billIds: parseIds(billIds),
      userIds: parseIds(userIds),
      transactionIds,
      netsuiteIds,
      creditMemoIds,
      submissionStatuses: submissionStatusTypes.map(({ value }) => value),
      source: source === BillSource.ALL ? null : source,
      sourceIds: parseIds(sourceIds),
      sourceStartDate: formatDateForQuery(sourceDateRange[0]),
      sourceEndDate: formatDateForQuery(sourceDateRange[1]),
      submissionStartDate: formatDateForQuery(submissionDateRange[0]),
      submissionEndDate: formatDateForQuery(submissionDateRange[1]),
      clientIds: extractIds(clients),
      insurerIds: extractIds(insurers),
      partnershipIds: extractIds(partnerships),
      billingTypes: billingTypes.map(type => type.value),
    };

    setFilterParams(formattedData);
    filterToggle();
  };

  const onFormSubmit = (e: BaseSyntheticEvent<object>): void => {
    setPaginationModel({ page: 0, pageSize: 100 });
    handleSubmit(parseFormValues)(e);
  };

  const { handleSubmit, control, formState, reset, resetField, watch } =
    useForm<SubmissionAdvancedFiltersValues>({
      mode: 'onChange',
      defaultValues,
      criteriaMode: 'all',
      resolver: yupResolver(
        SubmissionAdvancedFiltersValidationSchema,
      ) as Resolver<SubmissionAdvancedFiltersValues>,
    });

  const { errors, isValid, isDirty } = formState;

  const handleSourceChange = (source: string | null): void => {
    if (source === null) {
      resetField('sourceIds', {
        defaultValue: [],
      });
    }
    if (source === BillSource.ADMIN_FEE_CLAIMS) {
      resetField('billingTypes', {
        defaultValue: [],
      });
    }
  };

  const handleReset = (): void => {
    reset(defaultValues);
    setFilterParams(defaultSubmissionManagementGridFilterModel);
    filterToggle();
  };

  const [disableSourceId, setDisableSourceId] = useState(true);
  const [disableBillingTypes, setDisableBillingTypes] = useState(false);
  const watchedSource = watch('source');

  useEffect(() => {
    const isSourceIdDisabled = watchedSource === BillSource.ALL;
    const isBillingTypesDisabled =
      watchedSource === BillSource.ADMIN_FEE_CLAIMS;
    setDisableSourceId(isSourceIdDisabled);
    setDisableBillingTypes(isBillingTypesDisabled);
  }, [watchedSource]);

  return (
    <form onSubmit={onFormSubmit}>
      <HHStack direction="row" spacing={2}>
        <HHStack
          direction="row"
          flexWrap="wrap"
          alignItems="flex-end"
          sx={{ gap: 4 }}
        >
          <CSVInputFilter
            control={control}
            errors={errors}
            name="submissionIds"
            label="Submission ID"
            showClearButton={true}
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="billIds"
            label="Bill ID"
            showClearButton={true}
          />
          <DateRangeFilter
            control={control}
            errors={errors}
            name="submissionDateRange"
            labelFrom="Submission From"
            labelTo="Submission To"
            tooltipText="Filter submissions where the submission date falls between the two selected dates."
          />
          <SubmissionStatusFilter control={control} errors={errors} />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="userIds"
            label="User ID"
            showClearButton={true}
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="transactionIds"
            label="Transaction ID"
            showClearButton={true}
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="netsuiteIds"
            label="NetSuite ID"
            showClearButton={true}
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="creditMemoIds"
            label="Credit Memo ID"
            showClearButton={true}
          />
          <SourceFilter
            control={control}
            errors={errors}
            onSourceChange={handleSourceChange}
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="sourceIds"
            label="Source ID"
            disabled={disableSourceId}
            showClearButton={true}
          />
          <DateRangeFilter
            control={control}
            errors={errors}
            name="sourceDateRange"
            labelFrom="Source From"
            labelTo="Source To"
            tooltipText="Filter submissions where the source date falls between the two selected dates."
          />
          <ClientFilter
            control={control}
            errors={errors}
            clients={clients}
            hasError={hasError}
          />
          <InsurerFilter
            control={control}
            errors={errors}
            insurers={insurers}
            hasError={hasError}
          />
          <PartnershipFilter
            control={control}
            errors={errors}
            partnerships={partnerships}
            hasError={hasError}
          />
          <BillingTypeFilter
            control={control}
            errors={errors}
            billingTypes={billingTypes}
            disabled={disableBillingTypes}
          />
        </HHStack>
        <HHStack direction="row" spacing={4} alignSelf="flex-end">
          <HHButton size="small" hhVariant="text" onClick={handleReset}>
            Reset
          </HHButton>
          <HHButton
            size="small"
            type="submit"
            hhVariant="contained"
            onClick={onFormSubmit}
            disabled={!isValid || !isDirty}
          >
            Apply
          </HHButton>
          <HHIconButton
            hhVariant="variant-bypass"
            size="medium"
            color="default"
            aria-label="close-advanced-filters"
            onClick={filterToggle}
          >
            <Close />
          </HHIconButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
